import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {ArrowsClockwise, PencilSimple, WarningCircle} from '../../icons';
import {Spinner} from '../../Spinner';
import {ClientEconomicsApi} from '../../../api/clientEconomicsApi';
import {
    STRIP_MANUAL, STRIP_OK, STRIP_SYNCING, STRIP_UNUSED, STRIP_WAITING, STRIP_WARN, syncMoment,
} from './state/escalafyProvenance';

// El texto de una conexión que no está OK: el MISMO que la fila de la conexión y el chip de la ficha.
export const ESCALAFY_STATUS_KEYS = {
    DISCONNECTED: 'client_panel.economy.escalafy.disconnected',
    ORG_NOT_FOUND: 'client_panel.economy.escalafy.disconnected',
    LIMITED_ROLE: 'client_panel.economy.escalafy.limited_role',
    PLAN_WITHOUT_API: 'client_panel.economy.escalafy.plan_without_api',
};

// Los errores de «Usar los datos de Escalafy» con salida conocida (los mismos que la card de la ficha).
const USE_ERROR_KEYS = {
    MANUAL_VERSION_TODAY: 'economics.escalafy.error.manual_version_today',
    NO_ESCALAFY_PROPOSAL: 'economics.escalafy.error.no_proposal',
};

/** «hoy, 01:15» / «ayer, 06:31» / «30 sept, 14:52». */
export function syncWhenLabel(isoDate, t, language, now = new Date()) {
    const moment = syncMoment(isoDate, language, now);
    if (!moment) return null;
    return t(`economics.escalafy.when.${moment.key}`, {
        time: moment.time, date: moment.date, interpolation: {escapeValue: false},
    });
}

/**
 * La franja de arriba de «Del negocio» cuando el cliente tiene Escalafy vinculado: que se vea, ANTES de
 * tocar un número, que hay datos que llegan solos (primera conexión real, Gótica, 01/10: «el usuario no
 * se da cuenta»). Dice si se está actualizando, de cuándo es el último dato, o por qué no actualiza.
 *
 * Con una carga a mano que le ganó a Escalafy, ofrece volver a lo automático (la misma acción que la
 * card de la ficha). `onSeeConnection` baja a la sección de la conexión.
 */
export function EscalafySourceStrip({state, escalafy, onSeeConnection, onUsed}) {
    const {t, i18n} = useTranslation();
    const language = i18n && i18n.language;
    const [using, setUsing] = useState(false);
    if (!state) return null;

    const when = (iso) => syncWhenLabel(iso, t, language);

    const useEscalafy = () => {
        const connectionId = escalafy && escalafy.connection_id;
        if (!connectionId || using) return;
        setUsing(true);
        ClientEconomicsApi.escalafyUse(connectionId).then((response) => {
            setUsing(false);
            if (!response || response.status !== 'OK') {
                const code = response && (response.error_code || response.status);
                toast.error(t(USE_ERROR_KEYS[code] || 'economics.error.generic'));
                return;
            }
            toast.success(t('economics.form.saved', {version: (response.data && response.data.version) || '—'}));
            if (onUsed) onUsed();
        }).catch(() => {
            setUsing(false);
            toast.error(t('economics.error.generic'));
        });
    };

    const renderText = () => {
        switch (state.kind) {
            case STRIP_SYNCING:
                return (
                    <>
                        <strong>{t('economics.escalafy.src.syncing_title')}</strong>{' '}
                        <span>{t('economics.escalafy.src.syncing_body')}</span>
                    </>
                );
            case STRIP_WARN:
                return (
                    <>
                        <strong>{t('economics.escalafy.src.warn_title')}</strong>{' '}
                        <span>{t(ESCALAFY_STATUS_KEYS[state.status] || ESCALAFY_STATUS_KEYS.DISCONNECTED)}</span>
                        {state.lastSyncAt && (
                            <>
                                {' '}
                                <span>{t('economics.escalafy.src.last', {when: when(state.lastSyncAt), interpolation: {escapeValue: false}})}</span>
                            </>
                        )}
                    </>
                );
            case STRIP_MANUAL:
                return (
                    <>
                        <strong>{t('economics.escalafy.src.manual_title')}</strong>{' '}
                        <span>{t('economics.escalafy.src.manual_body')}</span>
                    </>
                );
            case STRIP_UNUSED:
                return (
                    <>
                        <strong>
                            {t(state.keeps ? 'economics.escalafy.src.unused_keeps_title' : 'economics.escalafy.src.unused_title')}
                        </strong>
                        {state.reason && ' '}
                        {state.reason && <span>{t(`client_panel.economy.escalafy.warning.${state.reason}`)}</span>}
                    </>
                );
            case STRIP_WAITING:
                return <strong>{t('economics.escalafy.src.waiting')}</strong>;
            default: {
                const lead = state.count !== null && state.count !== undefined
                    ? t('economics.escalafy.src.ok_count', {count: state.count, total: state.total})
                    : t('economics.escalafy.src.ok');
                const detail = [
                    state.days ? t('economics.escalafy.src.ok_window', {count: state.days}) : null,
                    state.lastSyncAt
                        ? t('economics.escalafy.src.last', {when: when(state.lastSyncAt), interpolation: {escapeValue: false}})
                        : null,
                ].filter(Boolean).join(' · ');
                return (
                    <>
                        <strong>{lead}</strong>
                        {detail && <>{' '}<span>{detail}</span></>}
                    </>
                );
            }
        }
    };

    const renderMark = () => {
        if (state.kind === STRIP_WARN || state.kind === STRIP_UNUSED) return <WarningCircle size={15} weight="bold"/>;
        if (state.kind === STRIP_MANUAL) return <PencilSimple size={13} weight="bold"/>;
        return <ArrowsClockwise size={14} weight="bold" className={state.kind === STRIP_SYNCING ? 'eco-spin' : undefined}/>;
    };

    const tone = {
        [STRIP_SYNCING]: '-syncing', [STRIP_WARN]: '-warn', [STRIP_UNUSED]: '-warn', [STRIP_MANUAL]: '-manual',
        [STRIP_OK]: '-ok', [STRIP_WAITING]: '-ok',
    }[state.kind] || '';

    return (
        <div className={`eco-srcstrip ${tone}`} data-testid="eco-escalafy-strip" data-state={state.kind}
             role={state.kind === STRIP_SYNCING ? 'status' : undefined}>
            <span className="eco-srcstrip__mark" aria-hidden="true">{renderMark()}</span>
            <span className="eco-srcstrip__text">{renderText()}</span>
            <span className="eco-srcstrip__acts">
                {state.kind === STRIP_MANUAL && escalafy && escalafy.connection_id && (
                    <button type="button" className="pnl-btn pnl-btn--ghost pnl-btn--sm" onClick={useEscalafy}
                            disabled={using} aria-busy={using || undefined} data-testid="eco-escalafy-strip-use">
                        {using && <Spinner color="negative very-small"/>}
                        {t('client_panel.economy.escalafy.use')}
                    </button>
                )}
                {onSeeConnection && (
                    <button type="button" className="eco-link" onClick={onSeeConnection} data-testid="eco-escalafy-strip-see">
                        {t('economics.escalafy.src.see')}
                    </button>
                )}
            </span>
        </div>
    );
}

export default EscalafySourceStrip;
